import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import {
  IPatientBaseline,
  IPatientBaselineData,
} from "../../../../@types/Baselines";
import { MeasurementTypes } from "../../../../@types/Measurements";
import { IPatientInfo } from "../../../../@types/Patient";
import { ChartCard } from "../../../../components/ChartCard";
import { ChartConfig } from "../../../../components/ChartCard/ChartCard.types";
import { useMultipleMeasurements } from "../../../../hooks/queries/measurements";
import { usePatientInfo } from "../../../../hooks/queries/patients";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { getSourceLegends } from "../../../../utils/chartSourceUtils";
import {
  formatMeasurementAverageRangeDates,
  getDataDisplayPreferenceFromLocalStorage,
  getTrendLineDisplaySettingFromLocalStorage,
} from "../../../../utils/chartUtils";
import {
  formatLatestBloodPressure,
  formatLatestMeasurementDate,
  LONG_DATE_TIME_FORMAT,
} from "../../../../utils/dateFormatter";
import { getLatestBloodPressureValue } from "../../../../utils/measurementsUtils";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { MeasurementModal } from "../MeasurementModal";
import { MeasurementModalMobile } from "../MeasurementModalMobile";
import { BkvChartCard } from "./components/BkvChartCard/BkvChartCard";
import { FibricheckChartCard } from "./components/FibriCheckChartCard/FibriCheckChartCard";
import { FitbitChartCard } from "./components/FitbitChartCard/FitbitChartCard";
import { FitbitSleepChartCard } from "./components/FitbitSleepChartCard/FitbitSleepChartCard";
import { NebulizerChartCard } from "./components/NebulizerChartCard/NebulizerChartCard";
import { StepsChartCard } from "./components/StepsChartCard";

type MeasurementCardProps = {
  measurementType: MeasurementTypes;
  cardTitle: string;
  unit?: string;
  minValue?: number;
  maxValue?: number;
  openFev1Graph?: boolean;
  onFev1GraphOpen?: () => void;
  patient?: IPatientInfo;
  checkNewData?: (mostRecentDate: dayjs.Dayjs) => void;
};

export function MeasurementCard({
  cardTitle,
  measurementType,
  unit,
  minValue,
  maxValue,
  openFev1Graph,
  onFev1GraphOpen,
  patient,
  checkNewData,
}: MeasurementCardProps) {
  const { t } = useTranslation();

  if (measurementType === "bp_diastolic") {
    // Because we are now bringing all of the blood pressure measurements from one single graph, we are ignoring the sytolic and only caring about the diastolic since it will get the data for both
    return null;
  }

  const { openModal } = useModal();
  const { patientId } = useParams();
  const { isMobile } = useIsMobile();

  const [searchParams] = useSearchParams();

  const chartConfigs: ChartConfig[] = [];
  const allMeasurements: Map<string, any[]> = new Map<string, any[]>();
  const allPatientBaselines: Map<string, IPatientBaselineData> = new Map<
    string,
    IPatientBaselineData
  >();
  let mainValues: any = { min: 0, max: 0, average: 0, latest: 0 };
  let diastolicMainValues: any = { min: 0, max: 0, average: 0, latest: 0 };

  const measurementTypes: MeasurementTypes[] = [measurementType];
  if (measurementType === "bp_systolic") {
    measurementTypes.push("bp_diastolic");
  }

  const [showTrendLine, setShowTrendLine] = useState<boolean>(
    getTrendLineDisplaySettingFromLocalStorage(measurementType)
  );

  const {
    preferences,
    portalPreferences,
    unitPreferences,
    getDateRangeFilter,
  } = useUserPreferences();

  const { hideFvcRejected, hideFev1Rejected, hideFvcUsable, hideFev1Usable } =
    preferences;

  // Mirror the modal's data-display filter (e.g. "Highest of day") so the small
  // card reflects the same selection. Initialise from localStorage (the source
  // login writes to) so the first render after logging in is correct, then keep
  // it in sync with later context changes (e.g. toggling in the modal) — same
  // pattern as showTrendLine below.
  const [displayFilter, setDisplayFilter] = useState<string>(() =>
    getDataDisplayPreferenceFromLocalStorage(measurementType)
  );

  // Use React Query directly to get fresh ishltBaselineActive value
  const { patient: patientFromQuery } = usePatientInfo({ patientId });
  const ishltBaselineActive =
    patientFromQuery?.ishltBaselineActive ??
    patient?.ishltBaselineActive ??
    false;

  const { data, isInitialLoading, refetch } = useMultipleMeasurements({
    types: measurementTypes.filter(
      (item) =>
        item !== "nebulizer" &&
        item !== "fitbit_activity" &&
        item !== "fitbit_sleep"
    ),
    patientId,
    filter: displayFilter,
    hideFvcRejected,
    hideFev1Rejected,
    hideFvcUsable,
    hideFev1Usable,
  });

  const getTrendLineDisplaySettingFromContext = () => {
    try {
      const trendLinePreferences = JSON.parse(
        unitPreferences.trendLineDisplayPreferences
      );
      if (
        trendLinePreferences &&
        typeof trendLinePreferences[measurementType] === "boolean"
      ) {
        return trendLinePreferences[measurementType];
      }
    } catch {
      return false;
    }

    return false;
  };

  const getDisplayFilterFromContext = (): string => {
    try {
      const raw = unitPreferences?.dataDisplayPreference;
      if (!raw) return "";

      const dataPreferences = typeof raw === "string" ? JSON.parse(raw) : raw;

      if (typeof dataPreferences !== "object" || dataPreferences === null) {
        return "";
      }

      return dataPreferences[measurementType] || dataPreferences.default || "";
    } catch {
      return "";
    }
  };

  // showTrendLine and displayFilter are both initialised from localStorage,
  // which login updates directly. The context's unitPreferences, however, is
  // only seeded on first mount and is NOT refreshed on login, so it can be stale
  // right after logging in. Skipping the first run keeps the (fresh)
  // localStorage-derived initial values instead of overwriting them with the
  // stale context. The effect still reacts to later context changes (e.g.
  // toggling in the modal), which keeps the card in sync with the modal during
  // the session.
  const isFirstPreferencesSync = useRef(true);
  useEffect(() => {
    if (isFirstPreferencesSync.current) {
      isFirstPreferencesSync.current = false;
      return;
    }
    setShowTrendLine(getTrendLineDisplaySettingFromContext());
    setDisplayFilter(getDisplayFilterFromContext());
  }, [unitPreferences]);

  if (data && data.length > 0) {
    data.forEach((d: any, index: number) => {
      if (!d) return;

      const { type, measurements, mainMeasurementValues, patientBaselines } = d;

      const activeBaseline = (patientBaselines as IPatientBaseline[])?.find(
        (x: IPatientBaseline) => x.archived === false
      )?.baseline;

      allPatientBaselines.set(type, {
        activeBaseline,
        baselines: patientBaselines,
      });

      if (d.mainMeasurementValues.maxDate && checkNewData) {
        checkNewData(d.mainMeasurementValues.maxDate);
      }

      if (measurements && measurements.size > 0) {
        measurements.forEach((measurementsMap: any, key: string) => {
          const source = (key || "unknown").toLowerCase();
          const sourceLegend = getSourceLegends(type, source);

          if (measurementType === "bp_systolic" && source === "apple-health") {
            return;
          }

          allMeasurements.set(`${type}|${source}`, measurementsMap);
          chartConfigs.push({
            dot: sourceLegend.shape !== "line",
            dotShape: sourceLegend.shape,
            lineType: "monotone",
            yKey: type,
            color: sourceLegend.color,
            lineOpacity: sourceLegend.lineOpacity,
            min: minValue,
            max: maxValue,
            source,
          });

          if (index === 0) mainValues = mainMeasurementValues;

          if (index === 1 && type === "bp_diastolic") {
            diastolicMainValues = mainMeasurementValues;
          }
        });
      }
    });
  }

  const onClickToOpenMeasurement = () => {
    mixpanelActions.track(`User Action: Opened ${measurementType} graph`);
    if (measurementType === "fev1") {
      onFev1GraphOpen?.();
    }
    if (isMobile) {
      openModal(
        <MeasurementModalMobile
          title={cardTitle}
          patientId={patientId}
          minValue={minValue}
          maxValue={maxValue}
          measurementType={measurementType}
        />,
        { width: "100 vw", height: "100vh", showCloseButton: true }
      );
    } else {
      openModal(
        <MeasurementModal
          patientId={patientId}
          patient={patient}
          cardTitle={cardTitle}
          unit={unit}
          measurementType={measurementType}
          minValue={minValue}
          maxValue={maxValue}
        />,
        {
          width: "auto",
          height: "676px",
          showCloseButton: true,
          padding: "12px 0 0 24px",
        }
      );
    }
  };

  const handleCardClick = () => {
    onClickToOpenMeasurement();
  };

  useEffect(() => {
    const type = searchParams.get("type");

    if (type === measurementType) {
      handleCardClick();
    }
  }, [searchParams]);

  // The measurement query key does not include the data-display/quality
  // filters, so changing them in the modal won't refetch on its own — force it
  // here whenever any of those (or the date range) change.
  useEffect(() => {
    refetch();
  }, [
    portalPreferences.dateRangeFilter,
    displayFilter,
    hideFvcRejected,
    hideFev1Rejected,
    hideFvcUsable,
    hideFev1Usable,
  ]);

  useEffect(() => {
    if (openFev1Graph && measurementType === "fev1") {
      onClickToOpenMeasurement();
    }
  }, [openFev1Graph, measurementType]);

  const getBaselines = () => {
    if (measurementType === "bp_systolic") {
      const systolicBaselines =
        allPatientBaselines.get("bp_systolic")?.baselines;
      const diastolicBaselines =
        allPatientBaselines.get("bp_diastolic")?.baselines;

      return systolicBaselines?.concat(diastolicBaselines ?? []) ?? [];
    }
    return (
      allPatientBaselines
        .get(measurementType)
        ?.baselines?.filter((b) =>
          ishltBaselineActive ? b : !b.isISHLTBaseline
        ) ?? []
    );
  };

  if (measurementType === "arrhythmia_status") {
    return (
      <FibricheckChartCard
        data={
          (allMeasurements as Map<string, any[]>) || new Map<string, any[]>()
        }
        onClick={handleCardClick}
        isLoading={isInitialLoading}
      />
    );
  }

  if (measurementType === "bkv") {
    return (
      <BkvChartCard
        title={cardTitle}
        data={
          (allMeasurements as Map<string, any[]>) || new Map<string, any[]>()
        }
        unit={unit}
        onClick={handleCardClick}
        isLoading={isInitialLoading}
      />
    );
  }

  if (measurementType === "nebulizer") {
    return (
      <NebulizerChartCard
        patientId={patientId}
        title={cardTitle}
        onClick={handleCardClick}
      />
    );
  }

  if (measurementType === "fitbit_activity") {
    return (
      <FitbitChartCard
        patientId={patientId}
        title={cardTitle}
        onClick={handleCardClick}
      />
    );
  }

  if (measurementType === "daily_steps") {
    return (
      <StepsChartCard
        title={cardTitle}
        data={
          (allMeasurements as Map<string, any[]>) || new Map<string, any[]>()
        }
        isLoading={isInitialLoading}
        onClick={handleCardClick}
        specificValues={[
          {
            label: formatLatestMeasurementDate(mainValues.latestDate)?.trim()
              ? t("patientPage_card_latestWithDate", {
                  date: formatLatestMeasurementDate(mainValues.latestDate),
                })
              : t("patientPage_card_latest"),
            value: mainValues?.latest != null ? mainValues.latest : "-",
          },
          {
            label: t("patientPage_card_highest"),
            value: mainValues?.max != null ? mainValues.max : "-",
            tooltip: {
              title: mainValues?.max != null ? mainValues.max : "-",
              label: dayjs(mainValues.maxValueDate).format(
                LONG_DATE_TIME_FORMAT
              ),
            },
          },
          {
            label: t("patientPage_card_mean"),
            value:
              mainValues?.average != null
                ? Math.round(mainValues.average)
                : "-",
            tooltip: {
              title:
                mainValues?.average != null
                  ? Math.round(mainValues.average)
                  : "-",
              label: formatMeasurementAverageRangeDates(
                mainValues.minUnarchivedDate,
                mainValues.maxUnarchivedDate
              ),
            },
          },
        ]}
      />
    );
  }

  if (measurementType === "fitbit_sleep") {
    return (
      <FitbitSleepChartCard
        patientId={patientId}
        title={cardTitle}
        onClick={handleCardClick}
      />
    );
  }

  return (
    <div
      style={{ padding: isMobile ? "5px" : undefined }}
      onMouseLeave={() => {
        const event = new MouseEvent("mouseleave", { bubbles: true });
        document.querySelector(".recharts-wrapper")?.dispatchEvent(event);
      }}
    >
      <ChartCard
        showTrendLine={showTrendLine}
        title={cardTitle}
        data={
          (allMeasurements as Map<string, any[]>) || new Map<string, any[]>()
        }
        chartConfig={chartConfigs}
        isLoading={isInitialLoading}
        onClick={handleCardClick}
        specificValues={
          measurementType === "bp_systolic"
            ? [
                {
                  label: formatLatestBloodPressure(
                    mainValues,
                    diastolicMainValues
                  )
                    ? t("patientPage_card_latestWithDate", {
                        date: formatLatestBloodPressure(
                          mainValues,
                          diastolicMainValues
                        ),
                      })
                    : t("patientPage_card_latest"),
                  value: getLatestBloodPressureValue(
                    mainValues,
                    diastolicMainValues
                  )
                    ? getLatestBloodPressureValue(
                        mainValues,
                        diastolicMainValues
                      )
                    : "0",
                },
                {
                  label: t("patientPage_card_mean"),
                  value: `${
                    mainValues.average != null ? mainValues.average : "-"
                  }/${
                    diastolicMainValues.average != null
                      ? diastolicMainValues.average
                      : "-"
                  }`,
                },
              ]
            : [
                {
                  label: formatLatestMeasurementDate(
                    mainValues.latestDate
                  )?.trim()
                    ? t("patientPage_card_latestWithDate", {
                        date: formatLatestMeasurementDate(
                          mainValues.latestDate
                        ),
                      })
                    : t("patientPage_card_latest"),
                  value: mainValues?.latest != null ? mainValues.latest : "-",
                },
                {
                  label: t("patientPage_card_highest"),
                  value: mainValues?.max != null ? mainValues.max : "-",
                  tooltip: {
                    title: mainValues?.max != null ? mainValues.max : "-",
                    label: dayjs(mainValues.maxValueDate).format(
                      LONG_DATE_TIME_FORMAT
                    ),
                  },
                },
                {
                  label: t("patientPage_card_mean"),
                  value: mainValues?.average != null ? mainValues.average : "-",
                  tooltip: {
                    title:
                      mainValues?.average != null ? mainValues.average : "-",
                    label: formatMeasurementAverageRangeDates(
                      mainValues.minUnarchivedDate,
                      mainValues.maxUnarchivedDate
                    ),
                  },
                },
              ]
        }
        patientBaselines={getBaselines()}
        minValue={minValue}
        maxValue={maxValue}
        dateRange={getDateRangeFilter()}
      />
    </div>
  );
}
